import { Button } from '../../../components/buttons'
import { colorPalette } from '../../../style/colorPalette'
import ReloadIcon from '../../../components/icons/ReloadIcon'
import { MonthSelect } from '../../../components/toolbars/common'
import { IconButton, styled, Box } from '@mui/material'
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material'
import { useCallback, useState } from 'react'
import DateVO from '../../../../vo/DateVO'
import { intl } from '../../../../i18n'
import SearchConditionDialog from '../components/SearchConditionDialog'
import {
  ProjectClosingStatusSearchCondition,
  getDefaultSearchCondition,
} from '../hooks/useSearchCondition'
import { ColDef } from 'ag-grid-community'
import { SortedColumnState } from '../../../model/bulkSheetColumnSortState'
import FilterButton from '../../../containers/BulkSheetView/components/header/FilterButton'
import SortButton from '../../../containers/BulkSheetView/components/header/SortButton'
import ColumnSettingButton from '../../../containers/BulkSheetView/components/header/ColumnSettingButton'
import DisplayFormatButton from '../../../containers/BulkSheetView/components/header/DisplayFormatButton'
import FileExportButton from '../../../containers/BulkSheetView/components/header/FileExportButton'
import { useHistory } from 'react-router'
type Props = {
  loading: boolean
  refresh: () => void
  onMonthChange: (year: number, month: number) => void
  searchCondition: ProjectClosingStatusSearchCondition
  onSearchConditionChange: (
    condition: Partial<ProjectClosingStatusSearchCondition>
  ) => void
  onSearch: () => void
  projectCount?: number
  onClickExport?: () => void
  filteredColumns?: ColDef[]
  onDeleteFilteredColumn?: (column: ColDef) => void
  resetFilters?: () => void
  sortColumnsState?: SortedColumnState[]
  onDeleteSortedColumn?: (colId: string | ColDef<any>) => void
  onDeleteSortedAllColumns?: () => void
  onChangeSortColumnState?: (
    colId: string | ColDef<any>,
    sort: 'asc' | 'desc' | null
  ) => void
  onClickColumnSettingButton?: (e: any) => void
  columnSettingOpen?: boolean
  rowHeight?: number
  onChangeRowHeight?: (value: number) => void
}

const Header = ({
  loading,
  refresh,
  onMonthChange,
  searchCondition,
  onSearchConditionChange,
  onSearch,
  projectCount,
  onClickExport,
  filteredColumns,
  onDeleteFilteredColumn,
  resetFilters,
  sortColumnsState,
  onDeleteSortedColumn,
  onDeleteSortedAllColumns,
  onChangeSortColumnState,
  onClickColumnSettingButton,
  columnSettingOpen,
  rowHeight,
  onChangeRowHeight,
}: Props) => {
  const now = DateVO.now()
  const [currentDate, setCurrentDate] = useState<DateVO>(now)
  const [searchDialogOpen, setSearchDialogOpen] = useState(false)
  const history = useHistory()

  const handleMonthChange = useCallback(
    (searchCondition: { year: number; month: number }) => {
      const newDate = new DateVO(
        `${searchCondition.year}-${searchCondition.month}-01`
      )
      setCurrentDate(newDate)
      onMonthChange(searchCondition.year, searchCondition.month)
    },
    [onMonthChange]
  )

  const handlePreviousMonth = useCallback(() => {
    const prevMonth = currentDate.subtractMonths(1)
    setCurrentDate(prevMonth)
    onMonthChange(prevMonth.getYear(), prevMonth.getMonth())
  }, [currentDate, onMonthChange])

  const handleNextMonth = useCallback(() => {
    const nextMonth = currentDate.addMonths(1)
    setCurrentDate(nextMonth)
    onMonthChange(nextMonth.getYear(), nextMonth.getMonth())
  }, [currentDate, onMonthChange])

  const handleThisMonth = useCallback(() => {
    const thisMonth = DateVO.now()
    setCurrentDate(thisMonth)
    onMonthChange(thisMonth.getYear(), thisMonth.getMonth())
  }, [onMonthChange])

  const handleOpenSearchDialog = useCallback(() => {
    setSearchDialogOpen(true)
  }, [])

  const handleCloseSearchDialog = useCallback(() => {
    setSearchDialogOpen(false)
  }, [])

  const resetSearchCondition = useCallback(() => {
    onSearchConditionChange(getDefaultSearchCondition())
  }, [onSearchConditionChange])

  const handleTransitionToProjectClosingStatus = useCallback(() => {
    history.push('/taskActualWorkApprovalStatus')
  }, [history])

  return (
    <HeaderContainer>
      <Button onClick={refresh} disabled={loading} sx={reloadButtonStyle}>
        <ReloadIcon />
      </Button>

      <Button
        onClick={handleOpenSearchDialog}
        colorPattern="skyBlue"
        disabled={loading}
        sx={{
          border: 'none',
          cursor: 'pointer',
          fontSize: '12px',
          height: '28px',
        }}
      >
        {intl.formatMessage({ id: 'projectClosingStatus.detailSearch' })}
      </Button>

      <MonthNavigation>
        <MonthNavigationButtons>
          <ShiftButton onClick={handlePreviousMonth} disabled={loading}>
            <ArrowBackIos style={arrowIconStyle} />
          </ShiftButton>

          <MonthSelect
            year={currentDate.getYear()}
            month={currentDate.getMonth()}
            search={handleMonthChange}
          />

          <ShiftButton onClick={handleNextMonth} disabled={loading}>
            <ArrowForwardIos style={arrowIconStyle} />
          </ShiftButton>

          <ThisMonthButton onClick={handleThisMonth} disabled={loading}>
            {intl.formatMessage({ id: 'this.month' })}
          </ThisMonthButton>
        </MonthNavigationButtons>
      </MonthNavigation>

      {filteredColumns && onDeleteFilteredColumn && resetFilters && (
        <Center>
          <FilterButton
            filteredColumns={filteredColumns}
            onDeleteFilteredColumn={onDeleteFilteredColumn}
            resetFilters={resetFilters}
          />
        </Center>
      )}
      <Center>
        {sortColumnsState &&
          onDeleteSortedColumn &&
          onDeleteSortedAllColumns &&
          onChangeSortColumnState && (
            <SortButton
              onDeleteSortedColumn={onDeleteSortedColumn}
              onDeleteSortedAllColumns={onDeleteSortedAllColumns}
              onChangeSortColumnState={onChangeSortColumnState}
              sortColumnsState={sortColumnsState}
            />
          )}
      </Center>
      {onClickColumnSettingButton && (
        <ColumnSettingButton
          onClick={e => onClickColumnSettingButton(e)}
          open={columnSettingOpen || false}
        />
      )}
      {rowHeight && onChangeRowHeight && (
        <DisplayFormatButton
          height={rowHeight}
          onChangeHeight={onChangeRowHeight}
        />
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: '5px',
          marginRight: '15px',
        }}
      >
        {onClickExport && <FileExportButton onClickExport={onClickExport} />}
      </Box>
      <SearchConditionDialog
        open={searchDialogOpen}
        onClose={handleCloseSearchDialog}
        searchCondition={searchCondition}
        onChangeCondition={onSearchConditionChange}
        resetCondition={resetSearchCondition}
        search={onSearch}
        projectCount={projectCount}
      />
      <Button
        onClick={handleTransitionToProjectClosingStatus}
        colorPattern="yellow"
        disabled={loading}
        sx={TransitionStyle}
      >
        {intl.formatMessage({
          id: 'projectClosingStatus.transitionTaskActualWorkApprovalStatus',
        })}
      </Button>
    </HeaderContainer>
  )
}

const TransitionStyle = {
  border: 'none',
  cursor: 'pointer',
  fontSize: '12px',
  height: '28px',
  marginLeft: '30px',
}

const HeaderContainer = styled('div')({
  display: 'flex',
  margin: '10px 10px 5px 10px',
  overflowX: 'auto',
  overflowY: 'hidden',
  paddingBlock: '3px',
  alignItems: 'center',
})

const reloadButtonStyle = {
  background: colorPalette.monotone[1],
  border: `1px solid ${colorPalette.monotone[3]}`,
  borderRadius: '50%',
  height: '28px',
  width: '28px',
  marginRight: '20px',
  padding: '5px 8px',
  cursor: 'pointer',
  '&:hover': { background: colorPalette.monotone[1] },
}

const MonthNavigation = styled('div')({
  display: 'flex',
  marginLeft: '15px',
  flexDirection: 'column',
  alignItems: 'center',
})

const MonthNavigationButtons = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
})

const Center = styled('div')({
  alignItems: 'center !important',
})

const ShiftButton = styled(IconButton)({
  backgroundColor: colorPalette.monotone[0],
  border: `1px solid ${colorPalette.monotone[2]}`,
  borderRadius: '4px',
  width: '24px',
  height: '24px',
  padding: '4px',
  '&:hover': {
    backgroundColor: colorPalette.monotone[1],
  },
})

const ThisMonthButton = styled(Button)({
  backgroundColor: colorPalette.monotone[0],
  border: `1px solid ${colorPalette.monotone[2]}`,
  borderRadius: '4px',
  minWidth: '48px',
  height: '28px',
  fontSize: '12px',
  color: colorPalette.monotone[4],
  '&:hover': {
    backgroundColor: colorPalette.monotone[1],
  },
})

const arrowIconStyle = {
  color: colorPalette.monotone[5],
  fontSize: '14px',
}

export default Header
